import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ZoomAfbeelding.css';

/**
 * Afbeelding die bij klikken groot opent in een venster over de pagina.
 * In het venster: klik op de afbeelding = 2x inzoomen (scrollen om te
 * verschuiven), nog eens klikken = terug. Sluiten met ×, Esc of klik naast
 * de afbeelding. Werkt ook in de toets zelf: er wordt niets van de pagina
 * verlaten of geopend in een nieuw tabblad.
 *
 * Alle overige props (style, className, loading) gaan naar de kleine <img>.
 */
export default function ZoomAfbeelding({ src, alt = '', ...imgProps }) {
  const [open, setOpen] = useState(false);
  const [ingezoomd, setIngezoomd] = useState(false);
  // Breedte in px van de ingezoomde afbeelding. Een percentage werkt hier niet:
  // het kader krimpt om de afbeelding heen, dus "200%" bleef even groot.
  const [zoomBreedte, setZoomBreedte] = useState(0);
  const kaderRef = useRef(null);

  // Slepen om te verschuiven. Op de iPad (vergrendelde webview) scrolt het
  // kader niet uit zichzelf met een vinger, dus we schuiven zelf mee met de
  // pointer. Na een sleep telt de losgelaten tik niet als "uitzoomen".
  const sleep = useRef(null);
  const gesleept = useRef(false);

  const sluit = useCallback(() => { setOpen(false); setIngezoomd(false); }, []);

  // Op het kader, niet op de <img>: door de pointer capture bij het slepen komt
  // de click op het kader terecht en zou een tik op de afbeelding niets doen.
  const wisselZoom = (e) => {
    e.stopPropagation();
    if (gesleept.current) { gesleept.current = false; return; }
    const img = kaderRef.current?.querySelector('img');
    if (!ingezoomd && img) setZoomBreedte(Math.round(img.getBoundingClientRect().width * 2));
    setIngezoomd((z) => !z);
  };

  const sleepStart = (e) => {
    if (!ingezoomd || !kaderRef.current) return;
    const k = kaderRef.current;
    sleep.current = { x: e.clientX, y: e.clientY, links: k.scrollLeft, boven: k.scrollTop, bewogen: false };
    gesleept.current = false;
    try { k.setPointerCapture(e.pointerId); } catch (err) { /* oudere browser zonder pointer capture */ }
  };
  const sleepBeweeg = (e) => {
    const s = sleep.current;
    if (!s || !kaderRef.current) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) + Math.abs(dy) > 12) s.bewogen = true; // een vinger trilt wat bij een tik
    kaderRef.current.scrollLeft = s.links - dx;
    kaderRef.current.scrollTop = s.boven - dy;
  };
  const sleepStop = () => {
    if (sleep.current?.bewogen) gesleept.current = true;
    sleep.current = null;
  };

  // Na het inzoomen het midden van de afbeelding in beeld zetten.
  useEffect(() => {
    const k = kaderRef.current;
    if (!ingezoomd || !k) return;
    k.scrollLeft = (k.scrollWidth - k.clientWidth) / 2;
    k.scrollTop = (k.scrollHeight - k.clientHeight) / 2;
  }, [ingezoomd]);

  useEffect(() => {
    if (!open) return undefined;
    const opToets = (e) => { if (e.key === 'Escape') { e.stopPropagation(); sluit(); } };
    window.addEventListener('keydown', opToets, true);
    return () => window.removeEventListener('keydown', opToets, true);
  }, [open, sluit]);

  if (!src) return null;
  const { style, className, ...rest } = imgProps;

  return (
    <>
      <img
        src={src}
        alt={alt}
        {...rest}
        className={`za-klein ${className || ''}`.trim()}
        style={style}
        role="button"
        tabIndex={0}
        title="Klik om te vergroten"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
      />
      {open && createPortal(
        <div className="za-overlay" role="dialog" aria-modal="true" aria-label={alt || 'Afbeelding vergroot'} onClick={sluit}>
          <button type="button" className="za-sluit" aria-label="Sluiten" onClick={sluit}>×</button>
          <div
            ref={kaderRef}
            className={`za-kader ${ingezoomd ? 'za-kader-zoom' : ''}`}
            onClick={wisselZoom}
            onPointerDown={sleepStart}
            onPointerMove={sleepBeweeg}
            onPointerUp={sleepStop}
            onPointerCancel={sleepStop}
          >
            <img
              src={src}
              alt={alt}
              draggable={false}
              className={`za-groot ${ingezoomd ? 'za-groot-zoom' : ''}`}
              style={ingezoomd && zoomBreedte ? { width: zoomBreedte } : undefined}
              title={ingezoomd ? 'Klik om uit te zoomen' : 'Klik om verder in te zoomen'}
            />
          </div>
          <p className="za-hint">{ingezoomd ? 'Sleep of scroll om te verschuiven · klik om uit te zoomen' : 'Klik op de afbeelding om verder in te zoomen · Esc om te sluiten'}</p>
        </div>,
        document.body,
      )}
    </>
  );
}
